"use client";

import { Checkbox as CheckboxPrimitive } from "@base-ui/react/checkbox";
import { CheckIcon } from "lucide-react";
import type * as React from "react";
import { cn } from "@/lib/utils";

type CheckedState = boolean | "indeterminate";

type CheckboxProps = Omit<
  React.ComponentProps<typeof CheckboxPrimitive.Root>,
  "checked" | "defaultChecked"
> & {
  checked?: CheckedState;
  defaultChecked?: CheckedState;
};

function isIndeterminate(value: CheckedState | undefined) {
  return value === "indeterminate";
}

function toChecked(value: CheckedState | undefined) {
  return value === "indeterminate" ? false : value;
}

function Checkbox({
  checked,
  className,
  defaultChecked,
  indeterminate,
  ...props
}: CheckboxProps) {
  return (
    <CheckboxPrimitive.Root
      checked={toChecked(checked)}
      className={cn(
        "peer size-5 shrink-0 rounded-[6px] border border-white/10 bg-white/[0.04] shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 data-checked:border-emerald-500/40 data-checked:bg-emerald-500/10 data-checked:shadow-[0_0_18px_rgba(16,185,129,0.18),inset_0_1px_0_rgba(255,255,255,0.12)] dark:data-checked:border-emerald-400/50 dark:data-checked:bg-emerald-500/20 data-disabled:cursor-not-allowed data-disabled:opacity-40",
        className,
      )}
      data-slot="checkbox"
      defaultChecked={toChecked(defaultChecked)}
      indeterminate={
        indeterminate ??
        (isIndeterminate(checked) || isIndeterminate(defaultChecked))
      }
      {...props}
    >
      <CheckboxPrimitive.Indicator
        className="flex items-center justify-center text-emerald-400"
        data-slot="checkbox-indicator"
      >
        <CheckIcon className="size-3.5" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

export { Checkbox };
